<template>
  <div
    v-if="active.setlist"
    class="bg-sky-100 p-4 shadow-xl"
    :class="panelOpen ? 'setlist-bar-open' : 'setlist-bar-closed'"
    data-cy="sidebar"
    @dragover="(ev) => ev.preventDefault()"
  >
    <button
      class="float-right sm:mt-2 transition-transform"
      :class="{ 'rotate-90': !panelOpen }"
      data-cy="toggle-panel"
      @click="panelOpen = !panelOpen"
    >
      <Icon name="ri:menu-line" size="20" />
    </button>

    <div v-if="panelOpen">
      <h1>Active Set List</h1>
      <h2 class="text-base">
        <NuxtLink
          :to="`/setlists/${active.setlist.id}`"
          class="hover:underline"
          data-cy="to-setlist"
        >
          {{ active.setlist.service_date }} - {{ active.setlist.leader_name }}
        </NuxtLink>
      </h2>
      <h3 v-if="(active.setlist.participants ?? []).length" class="text-base">
        Team: {{ (active.setlist.participants ?? []).join(", ") }}
      </h3>

      <hr />

      <div class="flex flex-row">
        <div class="grow">
          <button
            type="button"
            class="italic"
            @click="showCandidates = !showCandidates"
          >
            <Icon
              name="ri:triangle-fill"
              size="12"
              class="transition"
              :class="{ 'rotate-180': showCandidates, 'rotate-90': !showCandidates }"
            />
            Candidates
          </button>
        </div>
        <div class="italic">
          <NuxtLink to="/songs" class="text-blue-500 hover:underline">
            Find songs
          </NuxtLink>
        </div>
      </div>

      <div v-if="showCandidates" class="rounded-lg bg-white">
        <draggable
          :model-value="filtered(slist?.sheets)"
          class="min-h-20"
          data-cy="sidebar-candidates"
          group="songs"
          animation="0"
          :sort="false"
          @change="(ev) => draggableChange(ev, null)"
        >
          <SetlistSidebarSong
            v-for="sheet in filtered(slist?.sheets)"
            :draggable="true"
            :key="sheet.id"
            :sheet="sheet"
          />
        </draggable>
      </div>

      <div class="mt-4">
        <div
          v-for="position in plist?.positions ?? []"
          :key="position.id"
          data-cy="sidebar-positions"
        >
          <ul class="flex flex-row list-disc ml-4">
            <li :class="{ 'font-bold': position.is_music }">
              {{ position.label }}
            </li>
            <hr class="grow" />
          </ul>
          <div v-if="position.is_music" class="rounded-lg bg-white mb-1">
            <draggable
              :model-value="filtered(slist?.sheets, position.id)"
              class="min-h-8"
              group="songs"
              animation="0"
              :sort="false"
              @change="(ev) => draggableChange(ev, position.id)"
            >
              <SetlistSidebarSong
                v-for="sheet in filtered(slist?.sheets, position.id)"
                :key="sheet.id"
                :draggable="true"
                :sheet="sheet"
                :current-position-id="position.id"
              />
            </draggable>
          </div>
        </div>
      </div>

      <div class="mt-12 flex flex-row-reverse">
        <button class="btn-gray" @click="active.setlist = null">Close</button>
      </div>
    </div>
    <div v-else class="setlist-bar-closed-header">
      <span class="inline sm:hidden">Active</span> Set List:
      <span class="font-bold">{{ active.setlist.service_date }}</span>
      <button class="btn-gray inline ml-8 sm:hidden" @click="active.setlist = null">
        Close
      </button>
    </div>
  </div>
  <div v-else />
</template>

<script setup lang="ts">
import { VueDraggableNext as draggable } from "vue-draggable-next"

import { api } from "@/services"
import {
  useActiveSetlistStore,
  useSetlistPositionlistStore,
  useSetlistSheetlistStore,
  useSetlistRefreshStore,
} from "@/stores/setlists"

import type { SetlistSheet } from "@/services/api"
type SetlistSheetType = SetlistSheet["type"]

const active = useActiveSetlistStore()
const positionlist = useSetlistPositionlistStore()
const sheetlist = useSetlistSheetlistStore()
const refreshSetlists = useSetlistRefreshStore()

const panelOpen = ref(window.innerWidth > 600)
const showCandidates = ref(true)

const plist = computed(() => {
  if (!active.setlist) return undefined

  return positionlist.get({ setlistId: active.setlist.id }).data.value
})

const slist = computed(() => {
  if (!active.setlist) return undefined

  return sheetlist.get({ setlistId: active.setlist.id }).data.value
})

function filtered(sheets?: SetlistSheet[], positionId?: string): SetlistSheet[] {
  if (!sheets) return []
  return sheets.filter((s) => s.setlist_position_id == positionId)
}

async function addSheetTo(sheet: SetlistSheet, positionId: string | null) {
  const newType: SetlistSheetType = (() => {
    if (!positionId) return "5:candidate"
    if (
      (slist.value?.sheets ?? []).every((s) => s.setlist_position_id !== positionId)
    ) {
      return "1:primary"
    }
    return sheet.type
  })()

  await useToaster(async () => {
    await api.setlists.updateSetlistSheet(sheet.setlist_id, sheet.id, {
      setlist_position_id: positionId,
      type: newType,
    })

    // patch the current object
    sheet.setlist_position_id = positionId
    sheet.type = newType

    await refreshSetlists.refresh({ setlistId: sheet.setlist_id })
  })
}

async function draggableChange(
  event: { added?: { element: SetlistSheet } },
  positionId: string | null,
) {
  if (event.added) {
    await addSheetTo(event.added.element, positionId)
  }
}
</script>

<style>
.setlist-bar-open {
  @apply sm:w-80 relative sm:absolute sm:right-0 sm:top-20 md:relative md:top-0;
}
.setlist-bar-closed {
  @apply sm:w-12 relative;
}
.setlist-bar-closed-header {
  @apply sm:absolute sm:rotate-90 sm:top-40 sm:-right-16 sm:w-44;
}
</style>
